import { type Competition, type Tournament } from '@clubspark-node/clubspark-tennis-types';
import { competitionEngine, tournamentEngine } from 'tods-competition-factory';

import { client } from 'config/graphql/client';
import { type COMPETITION_ENGINE, TOURNAMENT_ENGINE } from 'config/graphql/constants';
import { DEMO_MODE_INFO_MESSAGE } from 'config/graphql/errors';
import {
  ENTITY_MUTATION,
  GET_DRAFT_TOURNAMENTS_PAGINATED,
  GET_EVENTS_COSTS,
  GET_FULL_TOURNAMENT,
  GET_TOURNAMENT_ROLES,
  GET_TOURNAMENT_URLS,
  LINKABLE_TOURNAMENTS,
  SYNC_TOURNAMENT,
} from 'config/graphql/queries';
import { demoTournamentsMock } from 'mocks/demoTournaments/demoTournaments';
import { CloudError } from 'repository/CloudError';
import { NOT_IMPLEMENTED } from 'repository/messages';
import type Repository from 'repository/repository';
import { type CloudResult, type RepositoryCacheType } from 'repository/repository';
import { type DraftTournamentInterface } from 'typedefs/api/graphql/tournamentTypes';
import { type EventCost } from 'typedefs/event';
import {
  type DraftTournamentsPaginatedVariables,
  type LinkableTournament,
  type TournamentRolesResponseInterface,
  type TournamentUrlsResponse,
} from 'typedefs/tournamentTypes';
import { isStandalone } from 'utils/environment';
import { transform } from 'utils/tournamentEngineAdapter';

export interface TournamentRepository extends Repository<Tournament> {
  getDraftTournaments(
    paginationOptions: DraftTournamentsPaginatedVariables,
    cache?: RepositoryCacheType,
  ): Promise<CloudResult<{ draftTournaments: { items: DraftTournamentInterface[]; totalItems?: number } }>>;
  getLinkableTournaments(id: string, cache?: RepositoryCacheType): Promise<CloudResult<LinkableTournament[]>>;
  syncTournament(id: string): Promise<CloudResult<Tournament>>;
  getTournamentUrls(id: string, cache: RepositoryCacheType): Promise<CloudResult<TournamentUrlsResponse>>;
  saveTournament(
    id: string,
    engine: typeof TOURNAMENT_ENGINE | typeof COMPETITION_ENGINE,
    methods: { method: string; params: Record<string, any> }[],
  ): Promise<CloudResult<{ mutateTournament: { success: boolean; error?: boolean } }>>;
  getEventsCosts(
    eventIds: string[],
    personCsIds: string[],
    tournamentId: string,
    cache?: RepositoryCacheType,
  ): Promise<CloudResult<EventCost[]>>;
}

export class TournamentRepositoryImpl implements TournamentRepository {
  async getLocalTournaments(): Promise<Tournament[]> {
    const itemsMap = {};
    const { db } = await import('services/storage/db');
    const localTournaments: Tournament[] = (await db.findAllTournaments()) || [];
    demoTournamentsMock.items.forEach((t) => (itemsMap[t.tournamentId] = t));
    localTournaments.forEach((t) => (itemsMap[t.tournamentId] = t));
    return Object.values(itemsMap);
  }

  /**
   * @param id {string} tournament id
   * @param cache {RepositoryCacheType} cache settings to use
   * @desc responsible for getting the tournament from wherever it's needed. (server, cache, indexed db...)
   */
  async get(tournamentId: string, cache: RepositoryCacheType = 'cache-first'): Promise<CloudResult<Tournament>> {
    const isDemoMode = isStandalone();
    // if demo mode try to find tournament from indexed db, or get one from mocked data
    // important to get it from indexed db (if it exists) so the competition engine mutates that tournament which will
    // again be stored to indexed db
    if (isDemoMode) {
      const { db } = await import('services/storage/db');
      const items = await this.getLocalTournaments();
      const mockedTournamentIfDemoMode = isDemoMode
        ? items.find((item) => item.tournamentId === tournamentId)
        : undefined;
      const tournamentFromIndexedDb: Tournament = await db.findTournament(tournamentId);
      return Promise.resolve({ data: tournamentFromIndexedDb || mockedTournamentIfDemoMode });
    }

    // catching the network error so it can be propagated to the service hook
    const graphqlResult = await client
      .query<{ fullTournament: Competition }>({
        query: GET_FULL_TOURNAMENT,
        fetchPolicy: cache,
        variables: {
          clubSparkTournamentId: tournamentId,
        },
      })
      .catch((error) => {
        const errorMessage = error?.networkError?.result?.errors[0]?.message || error.message;
        return { data: undefined, error: { name: 'networkError', message: errorMessage } };
      });

    const competition = graphqlResult?.data?.fullTournament;
    const tournament = competition?.tournaments?.find(
      (tournament: Tournament) => tournament.tournamentId.toUpperCase() === tournamentId.toUpperCase(),
    );
    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }
    return Promise.resolve({ data: transform(tournament, competition) });
  }

  /**
   * @param paginationOptions {DraftTournamentsPaginatedVariables} pagination
   * @param cache {RepositoryCacheType} cache settings to use
   * @desc fetch draft tournaments from the server or return them from mocked data
   */
  async getDraftTournaments(
    paginationOptions: DraftTournamentsPaginatedVariables,
    cache: RepositoryCacheType = 'cache-first',
  ): Promise<CloudResult<{ draftTournaments: { items: DraftTournamentInterface[]; totalItems?: number } }>> {
    const isDemoMode = isStandalone();

    if (isDemoMode) {
      const items: any[] = await this.getLocalTournaments();
      return Promise.resolve({ data: { draftTournaments: { items: items } } });
    }

    // catching the network error so it can be propagated to the service hook
    const graphqlResult = await client
      .query<{ draftTournaments: { items: DraftTournamentInterface[] } }>({
        query: GET_DRAFT_TOURNAMENTS_PAGINATED,
        fetchPolicy: cache,
        variables: { paginationOptions },
      })
      .catch((error) => {
        return { data: undefined, error: { name: 'networkError', message: error } };
      });
    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }

    return Promise.resolve({ data: graphqlResult.data });
  }

  async getLinkableTournaments(
    id: string,
    cache: RepositoryCacheType = 'network-only',
  ): Promise<CloudResult<LinkableTournament[]>> {
    if (isStandalone()) {
      const items = await this.getLocalTournaments();
      const data = items
        .filter((item) => item.tournamentId !== id)
        .map((item) => ({
          timings: { startDate: item.startDate, endDate: item.endDate },
          name: item.tournamentName,
          level: { name: 'demo' },
          id: item.tournamentId,
        }));
      return Promise.resolve({ data });
    }

    const graphqlResult = await client.query<{ linkableTournaments: LinkableTournament[] }>({
      query: LINKABLE_TOURNAMENTS,
      fetchPolicy: cache,
      variables: {
        tournamentId: id,
      },
    });
    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }
    return Promise.resolve({ data: graphqlResult.data.linkableTournaments });
  }

  async syncTournament(id: string): Promise<CloudResult<Tournament>> {
    if (isStandalone()) {
      return { error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) };
    }

    try {
      const { data } = await client.mutate({
        mutation: SYNC_TOURNAMENT,
        variables: { clubSparkTournamentId: id },
      });
      const competition = data?.syncWithDraftTournament;
      const tournament = data?.syncWithDraftTournament?.tournaments?.[0];
      return { data: transform(tournament, competition) };
    } catch (e: any) {
      if (e) return { error: new CloudError('sync-tournament-error', e.message) };
    }
  }

  save<T>(item: T) {
    console.log(item);
    return Promise.resolve({ error: NOT_IMPLEMENTED });
  }

  async saveTournament(
    id: string,
    engine: typeof TOURNAMENT_ENGINE | typeof COMPETITION_ENGINE,
    methods: { method: string; params: Record<string, any> }[],
  ): Promise<CloudResult<{ mutateTournament: { success: boolean; error?: boolean } }>> {
    const isDemoMode = isStandalone();
    // if there are linked tournaments, get all the IDs from competition engine, otherwise use single id from TD
    const { tournamentRecord } = tournamentEngine.getTournament({ tournamentId: id });
    const { tournamentRecords } = competitionEngine.getState();
    const tournamentIds =
      engine === TOURNAMENT_ENGINE ? tournamentRecord?.tournamentId : Object.keys(tournamentRecords);
    const action = engine === TOURNAMENT_ENGINE ? 'TOURNAMENT_ENGINE' : 'COMPETITION_ENGINE';
    const engineToUse = engine === TOURNAMENT_ENGINE ? tournamentEngine : competitionEngine;
    const payload = { executionQueue: methods, tournamentIds };
    const variables = { directive: { action, payload } };

    if (window['dev']?.params) {
      for (const method of methods) {
        if (window['dev'].params[method.method]) {
          method.params = { ...method.params, ...window['dev'].params[method.method] };
        }
      }
    }

    // handle regular requests
    if (!isDemoMode) {
      const mutationResult = await client.mutate({ mutation: ENTITY_MUTATION, variables });

      if (mutationResult.data) {
        if (mutationResult.data.mutateTournament?.error) {
          const mutationError = mutationResult.data.mutateTournament.error;
          return Promise.resolve({
            error: { code: mutationError.code ? mutationError.code : '', message: mutationError.message },
            data: mutationResult.data,
          });
        }

        engineToUse.executionQueue(methods);
        return Promise.resolve({ data: mutationResult.data });
      }

      return Promise.resolve({
        error: { code: mutationResult.errors[0].name, message: mutationResult.errors[0].message },
      });
    }

    // handle demo mode (just execute the queue locally and return success true so tournament service can store
    // the data to local db
    engineToUse.executionQueue(methods);
    return Promise.resolve({ data: { mutateTournament: { success: true } } });
  }
  async getTournamentUrls(
    id: string,
    cache: RepositoryCacheType = 'cache-first',
  ): Promise<CloudResult<TournamentUrlsResponse>> {
    if (isStandalone()) {
      return Promise.resolve({ error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) });
    }

    const graphqlResult = await client.query<TournamentUrlsResponse>({
      query: GET_TOURNAMENT_URLS,
      fetchPolicy: cache,
      variables: {
        clubSparkTournamentId: id,
      },
    });
    if (graphqlResult?.error?.message) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }
    return Promise.resolve({ data: graphqlResult.data });
  }

  async getEventsCosts(
    eventIds: string[],
    personCsIds: string[],
    tournamentId: string,
    cache: RepositoryCacheType = 'cache-first',
  ): Promise<CloudResult<EventCost[]>> {
    if (isStandalone()) {
      // return dummy value for standalone, should not display error
      return Promise.resolve({ data: [{ amountPaid: 0, amountPayableWithEvents: 0, personCsId: 'none' }] });
    }

    const graphqlResult = await client.query<{ eventCost: EventCost[] }>({
      query: GET_EVENTS_COSTS,
      fetchPolicy: cache,
      variables: { eventIds, personCsIds, tournamentId },
    });

    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }

    return Promise.resolve({ data: graphqlResult.data.eventCost });
  }

  async getTournamentRoles(
    id: string,
    cache: RepositoryCacheType = 'cache-first',
  ): Promise<CloudResult<TournamentRolesResponseInterface>> {
    if (isStandalone()) {
      return Promise.resolve({ error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) });
    }

    const graphqlResult = await client.query<TournamentRolesResponseInterface>({
      query: GET_TOURNAMENT_ROLES,
      fetchPolicy: cache,
      variables: {
        tournamentId: id,
      },
    });
    if (graphqlResult?.error?.message) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }
    return Promise.resolve({ data: graphqlResult.data });
  }
}
